import React, { useEffect, useState } from 'react';
import { AdminButton, AdminCard, AdminPageShell, AdminSelect, AdminStatusPill, AdminTable } from '../AdminUI';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const ExistingUsersPage = () => {
  const token = localStorage.getItem('adminToken');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchItems = async (status = statusFilter) => {
    setLoading(true);
    setError('');
    try {
      const query = status === 'all' ? '' : `?status=${encodeURIComponent(status)}`;
      const res = await fetch(`${API_BASE_URL}/admin/users${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load users');
      setItems(data.users || []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const updateUserStatus = async (userId, action) => {
    setActionLoadingId(userId);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Failed to ${action} user`);
      await fetchItems();
    } catch (err) {
      setError(err.message || `Failed to ${action} user`);
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <AdminPageShell
      title="Users"
      description="Manage user account health, verification, and suspension."
      actions={(
        <div className="min-w-48">
          <AdminSelect
            id="users-status-filter"
            value={statusFilter}
            onChange={(e) => {
              const next = e.target.value;
              setStatusFilter(next);
              fetchItems(next);
            }}
            className="border-white/50 bg-white/20 text-white focus:border-white focus:ring-white/25"
          >
            <option value="all" className="text-slate-900">All</option>
            <option value="active" className="text-slate-900">Active</option>
            <option value="suspended" className="text-slate-900">Suspended</option>
          </AdminSelect>
        </div>
      )}
    >
      {error ? <AdminCard className="border-rose-200 text-rose-600 dark:border-rose-900/40">{error}</AdminCard> : null}
      <AdminCard className="p-0">
        {loading ? (
          <div className="p-4 text-slate-500">Loading...</div>
        ) : (
          <AdminTable
            columns={[
              { key: 'username', label: 'Username' },
              { key: 'email', label: 'Email' },
              { key: 'phone', label: 'Phone', render: (row) => row.phone || '-' },
              { key: 'country', label: 'Country', render: (row) => row.country || '-' },
              { key: 'verified', label: 'Verified', render: (row) => <AdminStatusPill value={row.email_verified ? 'verified' : 'unverified'} /> },
              { key: 'status', label: 'Status', render: (row) => <AdminStatusPill value={row.account_status || 'active'} /> },
              { key: 'created_at', label: 'Joined', render: (row) => (row.created_at ? new Date(row.created_at).toLocaleString() : '-') },
              {
                key: 'actions',
                label: 'Actions',
                render: (row) => {
                  const isBusy = actionLoadingId === row.id;
                  const status = row.account_status || 'active';
                  return (
                    <div className="flex flex-wrap gap-2">
                      <AdminButton variant="secondary" className="px-2.5 py-1.5 text-xs" disabled={isBusy || status === 'active'} onClick={() => updateUserStatus(row.id, 'activate')}>
                        Activate
                      </AdminButton>
                      <AdminButton variant="danger" className="px-2.5 py-1.5 text-xs" disabled={isBusy || status === 'suspended'} onClick={() => updateUserStatus(row.id, 'suspend')}>
                        Suspend
                      </AdminButton>
                    </div>
                  );
                }
              }
            ]}
            rows={items}
            emptyMessage="No users found."
          />
        )}
      </AdminCard>
    </AdminPageShell>
  );
};

export default ExistingUsersPage;
